import { Button, ComboboxInternalProps, Stack, TextField, Typography } from '@chronosphereio/chrono-ui';
import TuneIcon from 'mdi-material-ui/Tune';
import numbro from 'numbro';
import { useCallback, useEffect, useState } from 'react';
import { ListVariableDefinition, VariableName } from '@/core';
import { useTemplateVariable, useTemplateVariableActions } from '@/dashboards/context';
import { MultiSelectVariable } from './MultiSelectVariable';
import { SingleSelectVariable } from './SingleSelectVariable';

type TemplateVariableProps = {
  name: VariableName;
  disabled?: boolean;
  onEditVariable?: (name: string) => void;
};

export function TemplateVariable({ name, disabled, onEditVariable }: TemplateVariableProps) {
  const ctx = useTemplateVariable(name);
  const kind = ctx.definition?.kind;
  switch (kind) {
    case 'TextVariable':
      return <TextVariable name={name} disabled={disabled} />;
    case 'ListVariable':
      return <ListVariable name={name} disabled={disabled} onEditVariable={onEditVariable} />;
    case 'IntervalVariable':
      return <IntervalVariable name={name} disabled={disabled} />;
  }

  return <div>Unsupported Variable Kind: ${kind}</div>;
}

function ListVariable({ name, disabled, onEditVariable }: TemplateVariableProps) {
  const ctx = useTemplateVariable(name);
  const definition = ctx.definition as ListVariableDefinition;
  const { setVariableValue } = useTemplateVariableActions();

  const allowMultiple = definition?.spec.allow_multiple === true;
  const allowAllValue = definition?.spec.allow_all_value === true;
  const title = definition?.spec.display?.name ?? name;

  if (ctx.state === undefined) {
    throw new Error(`Unable to find list variable named ${name}`);
  }

  const renderFooter = useCallback(
    (comboboxProps: ComboboxInternalProps) => (
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {numbro(comboboxProps.filteredOptions.length).format({ thousandSeparated: true })} values
        </Typography>
        {onEditVariable && (
          <Button
            variant="tertiary"
            size="small"
            StartIcon={<TuneIcon />}
            onClick={() => {
              comboboxProps.handleClose();
              onEditVariable(name);
            }}
          >
            Edit variable
          </Button>
        )}
      </Stack>
    ),
    [onEditVariable, name]
  );

  const { options, loading, value, hasLoaded } = ctx.state;

  if (allowMultiple === false) {
    return (
      <SingleSelectVariable
        title={title}
        options={options}
        isLoading={loading}
        allowAllValue={allowAllValue}
        // TODO: Fix state types?
        value={value as string | null}
        hasLoaded={hasLoaded || false}
        disabled={disabled}
        onChange={(nextValue) => {
          setVariableValue(name, nextValue);
        }}
        renderFooter={onEditVariable ? renderFooter : undefined}
      />
    );
  }

  // TODO: Can we improve state types so that we don't have to coerce things here for multi-selects?
  let multiValue: string[];
  if (value === null) {
    multiValue = [];
  } else if (Array.isArray(value)) {
    multiValue = value;
  } else {
    multiValue = [value];
  }

  return (
    <MultiSelectVariable
      title={title}
      options={options}
      isLoading={loading}
      allowAllValue={allowAllValue}
      value={multiValue}
      disabled={disabled}
      onChange={(nextValue) => {
        setVariableValue(name, nextValue);
      }}
      renderFooter={onEditVariable ? renderFooter : undefined}
    />
  );
}

function TextVariable({ name, disabled }: TemplateVariableProps) {
  const { state, definition } = useTemplateVariable(name);
  const [tempValue, setTempValue] = useState(state?.value ?? '');
  const { setVariableValue } = useTemplateVariableActions();

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTempValue(state?.value ?? '');
  }, [state?.value]);

  return (
    <TextField
      value={tempValue}
      onChange={(e) => setTempValue(e.target.value)}
      onBlur={() => setVariableValue(name, tempValue)}
      placeholder={name}
      label={definition?.spec.display?.name ?? name}
      disabled={disabled}
    />
  );
}

function IntervalVariable({ name, disabled }: TemplateVariableProps) {
  const ctx = useTemplateVariable(name);
  const definition = ctx.definition;
  if (definition?.kind !== 'IntervalVariable') {
    throw new Error(`Invalid variable kind: ${definition?.kind}`);
  }
  const { setVariableValue } = useTemplateVariableActions();

  let value = '';
  if (typeof ctx.state?.value === 'string') {
    value = ctx.state?.value;
  }

  return (
    <SingleSelectVariable
      hasLoaded={true}
      allowAllValue={false}
      onChange={(nextValue) => {
        setVariableValue(name, nextValue);
      }}
      isLoading={false}
      value={value}
      title={name}
      options={ctx.state?.options ?? []}
      disabled={disabled}
    />
  );
}
